/**
 * Build the level without a browser.
 *
 * Every headless tool used to open with the same fifteen lines -- make a
 * Builder, import each level module, set its owner and terrace, run it,
 * shrug off the ones that fail -- and reach.mjs and inspect.mjs had already
 * drifted on the details once (which modules, which grade policy).  A tool
 * reporting on a world the game does not build is worse than no tool, so the
 * preamble lives here and the tools import it.
 *
 * Node-only by construction: nothing here touches a scene, a canvas or the
 * DOM.  finish() is never called -- the colliders, surfaces and markers are
 * the whole point.
 */
import { Builder } from "./builder.js";
import { ColliderGrid } from "../collision.js";
import { gradeFor } from "./spec.js";
import { surfaceLookup } from "./index.js";

/**
 * The modules the game assembles, in the game's order (level/index.js).
 * "hero/index" rather than the split hero parts: the index grades and
 * places its own sections, and a tool that loaded them separately would be
 * reporting on a hero house nobody plays.
 */
export const HEADLESS_MODULES = ["exterior/index"];

/**
 * Assemble the level and hand back everything a tool asks of it.
 *
 * Per-module try/catch on purpose, kept from reach.mjs: one broken module
 * should cost the report a module, not the whole report.  The failures come
 * back in `skipped` (name plus the first line of the error) rather than being
 * printed here -- a library that writes to stdout decides the shape of every
 * caller's output, and reach.mjs's output is a deployment gate that must not
 * change by a byte.
 *
 * Returns { colliders, surfaces, markers, surfaceY, grid, loaded, skipped }:
 * surfaceY is the same surfaceLookup the game floors on -- paving and sod
 * included, or every tool stands the cat a few centimetres low -- and grid is
 * the same 4 m ColliderGrid broadphase the player queries.
 */
export async function buildHeadless({ modules = HEADLESS_MODULES } = {}) {
  const b = new Builder();
  const loaded = [];
  const skipped = [];
  for (const name of modules) {
    try {
      const mod = await import(`./${name}.js`);
      b.setOwner(name);
      b.setGrade(gradeFor(name));        // same terraces the game builds on
      (mod.build ?? mod.default)(b);
      loaded.push(name);
    } catch (e) {
      skipped.push({ name, message: e.message.split("\n")[0] });
    }
  }
  return {
    colliders: b.colliders,
    surfaces: b.surfaces,
    markers: b.markers,
    surfaceY: surfaceLookup(b.surfaces),
    grid: new ColliderGrid(b.colliders, 4),
    loaded,
    skipped,
  };
}
